import type { OptionsDocument, QuoteDocument } from 'paintscout'
import { extractTranslatableContent } from './extract-translatable-content'
import { contentHash } from './content-hash'

export type TranslationCompletionStatus = 'complete' | 'partial' | 'pending'

export interface TranslationLangStatus {
  lang: string
  status: TranslationCompletionStatus
}

/**
 * Returns the pre-bake completion status for each enabled translation language.
 *
 * - complete: all translatable strings (or none) have a pre-baked entry in quote.translations[lang]
 * - partial:  some strings are translated but at least one is missing
 * - pending:  no pre-baked entries exist yet for this language
 *
 * Shared by the API (status reporting / pre-bake gating) and the app (download guard in
 * WorkOrderViewDialog) so both sides agree on what "complete" means — including the
 * `totalCount === 0` short-circuit for quotes with no translatable content.
 *
 * Pass `options` so the string set matches what the translate paths
 * (computeQuoteTranslations/applyQuoteTranslations) actually translate — including
 * composed work-order render strings. Without it, status counts too few strings and can
 * report "complete" while rendered content is still untranslated.
 */
export function getTranslationStatus({
  quote,
  langs,
  options
}: {
  quote: QuoteDocument
  langs: string[]
  options?: OptionsDocument
}): TranslationLangStatus[] {
  const allStrings = Array.from(extractTranslatableContent(quote, options))
  const currentHashes = new Set(allStrings.map((s) => contentHash(s)))
  const totalCount = currentHashes.size

  return langs.map((lang) => {
    const cached = quote.translations?.[lang] ?? {}
    const translatedCount = Object.keys(cached).filter((hash) => currentHashes.has(hash)).length

    let status: TranslationCompletionStatus
    if (totalCount === 0 || translatedCount === totalCount) {
      status = 'complete'
    } else if (translatedCount === 0) {
      status = 'pending'
    } else {
      status = 'partial'
    }

    return { lang, status }
  })
}

/** Same as getTranslationStatus but keyed by lang for convenient lookup in UI state. */
export function getTranslationStatusMap(args: {
  quote: QuoteDocument
  langs: string[]
  options?: OptionsDocument
}): Record<string, TranslationCompletionStatus> {
  return Object.fromEntries(getTranslationStatus(args).map(({ lang, status }) => [lang, status]))
}
